import { useEffect } from "react"
import styled from "styled-components"

import { Banner, CircularProgress, spacing } from "@minuthq/meatball-ui-react"
import { createFileRoute } from "@tanstack/react-router"
import { postVerifyEmail } from "src/domains/auth/auth-queries"
import { useMutation } from "src/domains/http/use-mutation"
import { useTranslate } from "src/domains/i18n/use-translate"
import backdropImage from "src/ui/assets/buildings-orange-brown.jpg"
import { ErrorPage } from "src/ui/error-page/error-page"
import { PageLayout } from "src/ui/page-layout/page-layout"
import z from "zod"

const searchSchema = z.object({
  token: z.string(),
  userId: z.string(),
})

export const Route = createFileRoute("/verify-email")({
  component: VerifyEmail,
  validateSearch: searchSchema,
  errorComponent: VerifyEmailLinkError,
})

function VerifyEmail() {
  const { token, userId } = Route.useSearch()
  const { tPlaceholder } = useTranslate()

  const {
    mutate: mutateVerifyEmail,
    isError,
    data,
  } = useMutation(postVerifyEmail)

  useEffect(() => {
    void mutateVerifyEmail({ token, user_id: userId })
  }, [mutateVerifyEmail, token, userId])

  return (
    <PageLayout
      title={tPlaceholder("Verify your email")}
      landscapeImageUrl={backdropImage}
      portraitImageUrl={backdropImage}
    >
      <VerifyEmailResult isVerified={data !== null} isError={isError} />
    </PageLayout>
  )
}

function VerifyEmailResult({
  isVerified,
  isError,
}: {
  isVerified: boolean
  isError: boolean
}) {
  const { tPlaceholder } = useTranslate()

  if (isError) {
    return (
      <Banner
        type="error"
        size="large"
        title={tPlaceholder("Could not confirm email")}
      >
        {tPlaceholder(
          "The link you clicked on has probably expired by now. To get a new link, log into our apps and follow the instructions to verify your email"
        )}
      </Banner>
    )
  }

  // Not yet verified covers both the initial render (before the effect fires)
  // and the in-flight request, so there's no flash of the success banner.
  if (!isVerified) {
    return (
      <SpinnerBox>
        <CircularProgress />
      </SpinnerBox>
    )
  }

  return (
    <Banner type="good" size="large">
      {tPlaceholder(
        "Your email has been verified. You can now close this page."
      )}
    </Banner>
  )
}

function VerifyEmailLinkError() {
  const { tPlaceholder } = useTranslate()

  return (
    <ErrorPage
      title={tPlaceholder("Invalid verification link")}
      description={tPlaceholder(
        "This email verification link is missing required information. Log into our apps and follow the instructions to get a new link."
      )}
    />
  )
}

const SpinnerBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${spacing.M};
`
